import React, { useState, useEffect, useMemo } from 'react';
import { modelRegistryAPI, getRocImageUrlByModelId, getConfusionImageUrlByModelId, getFeatureImportanceImageUrlByModelId } from '../api';
import { InfoTooltip, LoadingState, ErrorState, EmptyState } from './shared/UIComponents';
import './ModelComparison.css';

const ModelComparison = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selected models for comparison
  const [leftModelId, setLeftModelId] = useState('');
  const [rightModelId, setRightModelId] = useState('');
  const [leftModel, setLeftModel] = useState(null);
  const [rightModel, setRightModel] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Artifact preview state
  const [previewArtifact, setPreviewArtifact] = useState(null);

  // Load all models on mount
  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await modelRegistryAPI.getModelsRegistry();
      const modelsList = (data && data.models) || [];
      setModels(modelsList);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load full model details when selection changes
  useEffect(() => {
    const loadModelDetails = async () => {
      if (!leftModelId && !rightModelId) {
        setLeftModel(null);
        setRightModel(null);
        return;
      }

      setLoadingDetails(true);
      try {
        const [leftData, rightData] = await Promise.all([
          leftModelId ? modelRegistryAPI.getModelInfo(leftModelId).catch(() => null) : null,
          rightModelId ? modelRegistryAPI.getModelInfo(rightModelId).catch(() => null) : null,
        ]);
        
        // Merge with basic model info from list
        const leftBasic = models.find(m => m.id === leftModelId);
        const rightBasic = models.find(m => m.id === rightModelId);
        
        setLeftModel(leftData ? { ...leftBasic, ...leftData } : leftBasic || null);
        setRightModel(rightData ? { ...rightBasic, ...rightData } : rightBasic || null);
      } catch (err) {
        console.error('Error loading model details:', err);
      } finally {
        setLoadingDetails(false);
      }
    };

    loadModelDetails();
  }, [leftModelId, rightModelId, models]);

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (sizeMb) => {
    if (sizeMb == null || isNaN(sizeMb)) return 'N/A';
    if (sizeMb < 1) return `${(sizeMb * 1024).toFixed(1)} KB`;
    return `${Number(sizeMb).toFixed(2)} MB`;
  };

  const formatMetricValue = (value, isPercentage = false) => {
    if (value == null || isNaN(value)) return 'N/A';
    if (isPercentage) return `${(value * 100).toFixed(2)}%`;
    return value.toFixed(4);
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end - start;
    if (diffMs < 1000) return `${diffMs}ms`;
    if (diffMs < 60000) return `${(diffMs / 1000).toFixed(1)}s`;
    return `${(diffMs / 60000).toFixed(1)}min`;
  };

  // Determine which value is better for a given metric
  const getBetterValue = (leftVal, rightVal, higherIsBetter = true) => {
    if (leftVal == null && rightVal == null) return 'none';
    if (leftVal == null) return 'right';
    if (rightVal == null) return 'left';
    if (higherIsBetter) {
      return leftVal > rightVal ? 'left' : leftVal < rightVal ? 'right' : 'equal';
    } else {
      return leftVal < rightVal ? 'left' : leftVal > rightVal ? 'right' : 'equal';
    }
  };

  // Get task type from model
  const getTaskType = (model) => {
    return model?.metadata?.task_type || model?.task_type || 'unknown';
  };

  // Classification metrics
  const classificationMetrics = [
    { key: 'accuracy', label: 'Accuracy', isPercentage: true, higherIsBetter: true },
    { key: 'precision', label: 'Precision', isPercentage: true, higherIsBetter: true },
    { key: 'recall', label: 'Recall', isPercentage: true, higherIsBetter: true },
    { key: 'f1_score', label: 'F1 Score', isPercentage: true, higherIsBetter: true },
    { key: 'roc_auc', label: 'ROC AUC', isPercentage: true, higherIsBetter: true },
  ];

  // Regression metrics
  const regressionMetrics = [
    { key: 'r2_score', label: 'R² Score', isPercentage: false, higherIsBetter: true },
    { key: 'mean_squared_error', label: 'MSE', isPercentage: false, higherIsBetter: false },
    { key: 'mean_absolute_error', label: 'MAE', isPercentage: false, higherIsBetter: false },
    { key: 'root_mean_squared_error', label: 'RMSE', isPercentage: false, higherIsBetter: false },
  ];

  // Determine which metrics to show based on task types
  const metricsToShow = useMemo(() => {
    const leftTask = getTaskType(leftModel);
    const rightTask = getTaskType(rightModel);
    
    // If both are classification or at least one is
    if (leftTask === 'classification' || rightTask === 'classification') {
      if (leftTask === 'regression' || rightTask === 'regression') {
        // Mixed - show both
        return [...classificationMetrics, ...regressionMetrics];
      }
      return classificationMetrics;
    }
    
    if (leftTask === 'regression' || rightTask === 'regression') {
      return regressionMetrics;
    }
    
    // Default to showing all
    return [...classificationMetrics, ...regressionMetrics];
  }, [leftModel, rightModel]);

  const isMixedTaskComparison = useMemo(() => {
    const leftTask = getTaskType(leftModel);
    const rightTask = getTaskType(rightModel);
    return leftTask && rightTask && leftTask !== 'unknown' && rightTask !== 'unknown' && leftTask !== rightTask;
  }, [leftModel, rightModel]);

  // Get metric value from model
  const getMetricValue = (model, metricKey) => {
    if (!model || !model.metrics) return null;
    return model.metrics[metricKey] ?? null;
  };

  // Swap models
  const handleSwapModels = () => {
    const tempId = leftModelId;
    setLeftModelId(rightModelId);
    setRightModelId(tempId);
  };

  // Render comparison row
  const renderComparisonRow = (label, leftValue, rightValue, better = 'none', tooltip = null) => (
    <div className="comparison__row">
      <div className="comparison__label">
        {label}
        {tooltip && <InfoTooltip content={tooltip} />}
      </div>
      <div className={`comparison__value comparison__value--left ${better === 'left' ? 'comparison__value--better' : ''}`}>
        {leftValue}
      </div>
      <div className={`comparison__value comparison__value--right ${better === 'right' ? 'comparison__value--better' : ''}`}>
        {rightValue}
      </div>
    </div>
  );

  // Render artifact thumbnail
  const renderArtifactThumbnail = (model, type) => {
    if (!model) return <div className="comparison__artifact-placeholder">-</div>;
    
    let url;
    let label;
    switch (type) {
      case 'roc':
        url = getRocImageUrlByModelId(model.dataset_name, model.id);
        label = 'ROC Curve';
        break;
      case 'confusion':
        url = getConfusionImageUrlByModelId(model.dataset_name, model.id);
        label = 'Confusion Matrix';
        break;
      case 'feature-importance':
        url = getFeatureImportanceImageUrlByModelId(model.dataset_name, model.id);
        label = 'Feature Importance';
        break;
      default:
        return <div className="comparison__artifact-placeholder">-</div>;
    }

    return (
      <div 
        className="comparison__artifact-thumbnail"
        onClick={() => setPreviewArtifact({ url, label, modelId: model.id })}
        title={`View ${label}`}
      >
        <img 
          src={url} 
          alt={label}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.classList.add('comparison__artifact-thumbnail--error');
          }}
        />
        <span className="comparison__artifact-label">{label}</span>
      </div>
    );
  };

  if (loading) return <LoadingState message="Loading models for comparison..." />;
  if (error) return <ErrorState message={error} onRetry={loadModels} />;

  return (
    <div className="model-comparison">
      <header className="comparison__header">
        <h2 className="comparison__title">Model Comparison</h2>
        <p className="comparison__subtitle">
          Select two models to compare their performance, metrics, and artifacts side-by-side.
        </p>
      </header>

      {/* Model Selection */}
      <div className="comparison__selectors">
        <div className="comparison__selector">
          <label className="comparison__selector-label">Model A</label>
          <select
            value={leftModelId}
            onChange={(e) => setLeftModelId(e.target.value)}
            className="comparison__select"
          >
            <option value="">Select a model...</option>
            {models.map((model) => (
              <option 
                key={model.id} 
                value={model.id}
                disabled={model.id === rightModelId}
              >
                {model.id} ({model.model_type} - {model.dataset_name})
              </option>
            ))}
          </select>
        </div>

        <button 
          className="comparison__swap-btn"
          onClick={handleSwapModels}
          disabled={!leftModelId && !rightModelId}
          title="Swap models"
        >
          <span className="comparison__swap-icon">&#8644;</span>
        </button>

        <div className="comparison__selector">
          <label className="comparison__selector-label">Model B</label>
          <select
            value={rightModelId}
            onChange={(e) => setRightModelId(e.target.value)}
            className="comparison__select"
          >
            <option value="">Select a model...</option>
            {models.map((model) => (
              <option 
                key={model.id} 
                value={model.id}
                disabled={model.id === leftModelId}
              >
                {model.id} ({model.model_type} - {model.dataset_name})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Comparison Content */}
      {(!leftModelId && !rightModelId) ? (
        <EmptyState 
          title="Select Models to Compare"
          description="Choose two models from the dropdowns above to see a detailed side-by-side comparison."
        />
      ) : loadingDetails ? (
        <LoadingState message="Loading model details..." />
      ) : (
        <div className="comparison__content">
          {/* Basic Information */}
          <section className="comparison__section">
            <h3 className="comparison__section-title">Basic Information</h3>
            <div className="comparison__grid">
              {renderComparisonRow(
                'Model ID',
                leftModel?.id || '-',
                rightModel?.id || '-'
              )}
              {renderComparisonRow(
                'Model Type',
                leftModel?.model_type || '-',
                rightModel?.model_type || '-'
              )}
              {renderComparisonRow(
                'Dataset',
                leftModel?.dataset_name || '-',
                rightModel?.dataset_name || '-'
              )}
              {renderComparisonRow(
                'Task Type',
                getTaskType(leftModel) || '-',
                getTaskType(rightModel) || '-'
              )}
              {renderComparisonRow(
                'Version',
                leftModel?.version || '-',
                rightModel?.version || '-'
              )}
              {renderComparisonRow(
                'Created',
                formatDate(leftModel?.created_at),
                formatDate(rightModel?.created_at)
              )}
              {renderComparisonRow(
                'Model Size',
                formatFileSize(leftModel?.file_size_mb),
                formatFileSize(rightModel?.file_size_mb),
                getBetterValue(rightModel?.file_size_mb, leftModel?.file_size_mb, true), // Smaller is better
                'Smaller model sizes typically indicate simpler models'
              )}
            </div>
          </section>

          {/* Training Configuration */}
          <section className="comparison__section">
            <h3 className="comparison__section-title">Training Configuration</h3>
            <div className="comparison__grid">
              {renderComparisonRow(
                'Normalization',
                leftModel?.normalization_method || leftModel?.metadata?.normalization_method || '-',
                rightModel?.normalization_method || rightModel?.metadata?.normalization_method || '-'
              )}
              {renderComparisonRow(
                'Test Size',
                leftModel?.metadata?.train_test_split ? `${(leftModel.metadata.train_test_split * 100).toFixed(0)}%` : '-',
                rightModel?.metadata?.train_test_split ? `${(rightModel.metadata.train_test_split * 100).toFixed(0)}%` : '-'
              )}
              {renderComparisonRow(
                'Feature Count',
                leftModel?.feature_set?.length || leftModel?.metadata?.feature_names?.length || '-',
                rightModel?.feature_set?.length || rightModel?.metadata?.feature_names?.length || '-'
              )}
              {renderComparisonRow(
                'Data Shape',
                leftModel?.metadata?.data_shape ? `${leftModel.metadata.data_shape[0]} x ${leftModel.metadata.data_shape[1]}` : '-',
                rightModel?.metadata?.data_shape ? `${rightModel.metadata.data_shape[0]} x ${rightModel.metadata.data_shape[1]}` : '-'
              )}
              {renderComparisonRow(
                'Training Duration',
                formatDuration(leftModel?.metadata?.training_timestamp, leftModel?.created_at),
                formatDuration(rightModel?.metadata?.training_timestamp, rightModel?.created_at),
                'none',
                'Time taken to train the model'
              )}
            </div>
          </section>

          {/* Performance Metrics */}
          <section className="comparison__section">
            <h3 className="comparison__section-title">Performance Metrics</h3>
            {isMixedTaskComparison && (
              <div style={{ marginBottom: 12, padding: 10, border: '1px solid #f0c36d', borderRadius: 8, background: '#fff8e8' }}>
                You are comparing a classification model with a regression model. Metric rankings and winner badges are not directly comparable across task types.
              </div>
            )}
            <div className="comparison__grid">
              {metricsToShow.map(({ key, label, isPercentage, higherIsBetter }) => {
                const leftVal = getMetricValue(leftModel, key);
                const rightVal = getMetricValue(rightModel, key);
                const better = getBetterValue(leftVal, rightVal, higherIsBetter);
                
                return renderComparisonRow(
                  label,
                  formatMetricValue(leftVal, isPercentage),
                  formatMetricValue(rightVal, isPercentage),
                  better,
                  `${higherIsBetter ? 'Higher' : 'Lower'} is better`
                );
              })}
            </div>
          </section>

          {/* Cross-Validation Metrics (if available) */}
          {(leftModel?.metrics?.cv_neg_mean_squared_error || rightModel?.metrics?.cv_neg_mean_squared_error) && (
            <section className="comparison__section">
              <h3 className="comparison__section-title">Cross-Validation Metrics</h3>
              <div className="comparison__grid">
                {renderComparisonRow(
                  'CV MSE (mean)',
                  leftModel?.metrics?.cv_neg_mean_squared_error != null 
                    ? Math.abs(leftModel.metrics.cv_neg_mean_squared_error).toFixed(4) 
                    : '-',
                  rightModel?.metrics?.cv_neg_mean_squared_error != null 
                    ? Math.abs(rightModel.metrics.cv_neg_mean_squared_error).toFixed(4) 
                    : '-',
                  getBetterValue(
                    leftModel?.metrics?.cv_neg_mean_squared_error ? Math.abs(leftModel.metrics.cv_neg_mean_squared_error) : null,
                    rightModel?.metrics?.cv_neg_mean_squared_error ? Math.abs(rightModel.metrics.cv_neg_mean_squared_error) : null,
                    false
                  ),
                  'Lower is better'
                )}
                {renderComparisonRow(
                  'CV MSE (std)',
                  leftModel?.metrics?.cv_neg_mean_squared_error_std?.toFixed(4) || '-',
                  rightModel?.metrics?.cv_neg_mean_squared_error_std?.toFixed(4) || '-',
                  getBetterValue(
                    leftModel?.metrics?.cv_neg_mean_squared_error_std,
                    rightModel?.metrics?.cv_neg_mean_squared_error_std,
                    false
                  ),
                  'Lower variance indicates more stable model'
                )}
              </div>
            </section>
          )}

          {/* Artifacts */}
          <section className="comparison__section">
            <h3 className="comparison__section-title">Visualization Artifacts</h3>
            <div className="comparison__artifacts">
              <div className="comparison__artifacts-row">
                <div className="comparison__artifacts-label">ROC Curve</div>
                <div className="comparison__artifacts-cell">
                  {renderArtifactThumbnail(leftModel, 'roc')}
                </div>
                <div className="comparison__artifacts-cell">
                  {renderArtifactThumbnail(rightModel, 'roc')}
                </div>
              </div>
              <div className="comparison__artifacts-row">
                <div className="comparison__artifacts-label">Confusion Matrix</div>
                <div className="comparison__artifacts-cell">
                  {renderArtifactThumbnail(leftModel, 'confusion')}
                </div>
                <div className="comparison__artifacts-cell">
                  {renderArtifactThumbnail(rightModel, 'confusion')}
                </div>
              </div>
              <div className="comparison__artifacts-row">
                <div className="comparison__artifacts-label">Feature Importance</div>
                <div className="comparison__artifacts-cell">
                  {renderArtifactThumbnail(leftModel, 'feature-importance')}
                </div>
                <div className="comparison__artifacts-cell">
                  {renderArtifactThumbnail(rightModel, 'feature-importance')}
                </div>
              </div>
            </div>
          </section>

          {/* Summary / Winner Badge */}
          {leftModel && rightModel && (
            <section className="comparison__section comparison__summary">
              <h3 className="comparison__section-title">Quick Summary</h3>
              <div className="comparison__summary-content">
                {(() => {
                  if (isMixedTaskComparison) {
                    return (
                      <div className="comparison__winner comparison__winner--tie">
                        <span className="comparison__winner-badge">Incomparable</span>
                        <span className="comparison__winner-text">
                          Cross-task comparison detected. Compare within the same task type for a reliable winner.
                        </span>
                      </div>
                    );
                  }
                  // Calculate wins for each model
                  let leftWins = 0;
                  let rightWins = 0;
                  
                  metricsToShow.forEach(({ key, higherIsBetter }) => {
                    const leftVal = getMetricValue(leftModel, key);
                    const rightVal = getMetricValue(rightModel, key);
                    const better = getBetterValue(leftVal, rightVal, higherIsBetter);
                    if (better === 'left') leftWins++;
                    if (better === 'right') rightWins++;
                  });

                  if (leftWins > rightWins) {
                    return (
                      <div className="comparison__winner comparison__winner--left">
                        <span className="comparison__winner-badge">Model A</span>
                        <span className="comparison__winner-text">
                          performs better on {leftWins} of {metricsToShow.length} metrics
                        </span>
                      </div>
                    );
                  } else if (rightWins > leftWins) {
                    return (
                      <div className="comparison__winner comparison__winner--right">
                        <span className="comparison__winner-badge">Model B</span>
                        <span className="comparison__winner-text">
                          performs better on {rightWins} of {metricsToShow.length} metrics
                        </span>
                      </div>
                    );
                  } else {
                    return (
                      <div className="comparison__winner comparison__winner--tie">
                        <span className="comparison__winner-badge">Tie</span>
                        <span className="comparison__winner-text">
                          Both models perform equally based on available metrics
                        </span>
                      </div>
                    );
                  }
                })()}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Artifact Preview Modal */}
      {previewArtifact && (
        <div 
          className="comparison__modal-overlay"
          onClick={() => setPreviewArtifact(null)}
        >
          <div 
            className="comparison__modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="comparison__modal-header">
              <h4>{previewArtifact.label} - {previewArtifact.modelId}</h4>
              <button 
                className="comparison__modal-close"
                onClick={() => setPreviewArtifact(null)}
              >
                &times;
              </button>
            </div>
            <div className="comparison__modal-body">
              <img 
                src={previewArtifact.url} 
                alt={previewArtifact.label}
                className="comparison__modal-image"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelComparison;
